import React, { useCallback } from 'react';
import i18next from 'i18next';
import ToggleItem from './toggle-item';
import { Maybe, ContactOptions, Branch, Vehicle, Ecommerce } from '../../../@types/codegen/types';
import { formatPhonenumber } from '../../../utils/phonenumbers';
import PubSub from '../../../utils/PubsubItem';
import { notEmpty } from '../../../utils/formats';
import { RepeatTiny } from '../../../StyledComponents/Repeat';
import { ButtonContent, ButtonPrimary, ButtonSecondary } from '../../../StyledComponents/Button';
import { ConversionOption } from '../../../@types/Search';
import { LeadForm } from './LeadForm';

interface ActionListProps {
  vehicle: Vehicle;
  ecommerce?: Ecommerce | null;
  branch?: Maybe<Branch>;
  contact?: Maybe<ContactOptions>;
  conversionOptions?: ConversionOption[];
  startEcom?: () => void;
}

const ActionList = ({
  vehicle,
  ecommerce,
  contact,
  conversionOptions,
  branch,
  startEcom,
}: ActionListProps) => {
  const email = contact?.email || branch?.contact?.email;
  const emailSubject = encodeURIComponent(
    `${i18next.t('item.actions.emailSubject', {
      hostName: location.hostname,
      vehicle: `${vehicle.data.registrationNumber}, ${vehicle.data.manufacturer} ${vehicle.data.modelSeries}`,
    })}`
  );
  const phonenumber = contact?.phonenumber || branch?.contact?.phonenumber;

  const onClickMailVisible = useCallback(() => PubSub.publish('MailVisible'), []);
  const onClickPhoneVisible = useCallback(() => PubSub.publish('PhonenumberVisible'), []);
  const onClickPhoneValue = useCallback(() => PubSub.publish('PhonenumberCall'), []);

  const onFormClick = (form_reference: string) => {
    const formContainer = document.getElementById(`form-conversion-option-${form_reference}`);
    if (formContainer) {
      const form = formContainer.querySelector('form');
      if (form) {
        if (vehicle.data.registrationNumber) {
          const idInput = form.querySelector<HTMLInputElement>('[name=Registreringsnummer]');
          if (idInput) {
            idInput.value = vehicle.data.registrationNumber;
          }
        }
        const regInput = form.querySelector<HTMLInputElement>('[name=Id]');
        if (regInput) {
          regInput.value = vehicle.id;
        }
      }
      formContainer.classList.add('modal--is-open');
    }
  };

  if (!conversionOptions?.length) {
    return null;
  }

  const options = conversionOptions
    .sort((a, b) => a.order - b.order)
    .filter((x) => (x.type === 'ecom' ? !!ecommerce?.enabled || !!ecommerce?.reserved : true))
    .map((option, i) => {
      const primary = i === 0;
      switch (option.type) {
        case 'form':
          return (
            <RepeatTiny key={`form-${option.form_reference}`}>
              {primary ? (
                <ButtonPrimary
                  onClick={() => onFormClick(option.form_reference!)}
                  title={option.name}
                  fullWidth
                >
                  <ButtonContent>{option.name || i18next.t('item.actions.openform')}</ButtonContent>
                </ButtonPrimary>
              ) : (
                <ButtonSecondary
                  onClick={() => onFormClick(option.form_reference!)}
                  title={option.name}
                  fullWidth
                >
                  <ButtonContent>{option.name || i18next.t('item.actions.openform')}</ButtonContent>
                </ButtonSecondary>
              )}
            </RepeatTiny>
          );
        case 'ecom':
          return ecommerce ? (
            <React.Fragment key="ecom">
              {ecommerce && ecommerce.enabled && (
                <RepeatTiny>
                  {primary ? (
                    <ButtonPrimary
                      disabled={!!ecommerce.reserved}
                      fullWidth
                      onClick={() => startEcom?.()}
                    >
                      <ButtonContent>
                        {option.name || i18next.t('item.actions.buyOnline')}
                      </ButtonContent>
                    </ButtonPrimary>
                  ) : (
                    <ButtonSecondary
                      disabled={!!ecommerce.reserved}
                      fullWidth
                      onClick={() => startEcom?.()}
                    >
                      <ButtonContent>
                        {option.name || i18next.t('item.actions.buyOnline')}
                      </ButtonContent>
                    </ButtonSecondary>
                  )}
                </RepeatTiny>
              )}
            </React.Fragment>
          ) : undefined;
        case 'email':
          return email ? (
            <ToggleItem
              key="email"
              onClickVisible={onClickMailVisible}
              title={option.name || i18next.t('item.actions.showEmail')}
              value={email}
              type="mailto"
              href={`${email}?subject=${emailSubject}`}
              primary={primary}
            />
          ) : undefined;

        case 'phone':
          return phonenumber ? (
            <ToggleItem
              key="phone"
              title={i18next.t('item.actions.showPhoneNumber')}
              value={formatPhonenumber(phonenumber)}
              type="tel"
              onClickVisible={onClickPhoneVisible}
              onClickValue={onClickPhoneValue}
              primary={primary}
            />
          ) : undefined;

        default:
          break;
      }
    })
    .filter(notEmpty);

  return <>
    <LeadForm branchId={vehicle.branch?.id} itemId={vehicle.id} />
    {options}</>;
};

export default ActionList;
